import {
	type FormField as SchemaFormField,
	type FormFieldNestedList as SchemaFormFieldNestedList,
	type FormFieldSelectList as SchemaFormFieldSelectList,
	type FormFieldSubForm as SchemaFormFieldSubForm,
	type FormMetadata as SchemaFormMetadata,
	IkeFieldType,
	type IkeProviderCustom,
	IkeProviderType,
	type FormFieldSubFormSchema,
} from '@ikegps/core-api';
import { FormModel } from '../form';
import {
	FormModelField,
	FormModelFieldNestedList,
	FormModelFieldSelectList,
	IkeCalcExistingSpanLegacyProvider,
} from '../index';
import { FormModelFieldSubForm } from '../subform/form.subform';

export function readMetadata(target: FormModel, metadata: SchemaFormMetadata) {
	if (metadata == null) {
		return;
	}

	if (metadata.import != null) {
		target.metadata.setImportType(metadata.import.type, metadata.import.version);
	}

	if (metadata.status != null) {
		const statusField = target.getField<FormModelFieldNestedList>(metadata.status.field);
		if (
			statusField == null ||
			!(statusField.type === IkeFieldType.NESTED_LIST || statusField.type === IkeFieldType.SELECT_LIST)
		) {
			throw new Error('Unable to find status field for form: ' + target.revision + ' status:' + metadata.status.field);
		}
		target.metadata.setStatusField(statusField);
	}

	if (metadata.mapColor != null) {
		const mapColorField = target.getField<FormModelFieldNestedList | FormModelFieldSelectList>(metadata.mapColor.field);
		if (
			mapColorField == null ||
			!(mapColorField.type === IkeFieldType.NESTED_LIST || mapColorField.type === IkeFieldType.SELECT_LIST)
		) {
			throw new Error(
				'Unable to find status field for form: ' + target.revision + ' mapColor:' + metadata.mapColor.field
			);
		}
		target.metadata.setMapColorField(mapColorField);
	}

	if (metadata.address_lookup != null) {
		const addressLookupField = target.getField<FormModelField>(metadata.address_lookup.address.field);
		if (
			addressLookupField == null ||
			!(addressLookupField.type === IkeFieldType.TEXT || addressLookupField.type === IkeFieldType.NUMBER)
		) {
			throw new Error(
				'Unable to find address lookup for form ' +
					target.revision +
					' address_lookup: ' +
					metadata.address_lookup.address.field
			);
		}
		target.metadata.setAddressLookupField(addressLookupField);
	} else {
		target.metadata.setAddressLookupField(null);
	}
	Object.keys(metadata).forEach((key) => {
		if (key === 'import' || key === 'status' || key === 'mapColor') {
			return;
		}

		target.metadata.setOption(key, metadata[key]);
	});
}

function getProviders(field: SchemaFormField): IkeProviderCustom[] {
	const providers: IkeProviderCustom[] = [];

	// No valid providers nothing to serialize
	if (field.providers == null || field.providers.length === 0) {
		return undefined;
	}
	// Convert all string providers to their respective object mode
	for (const provider of field.providers) {
		if (typeof provider !== 'string') {
			providers.push(provider);
			continue;
		}
		// Default provider object for existing span
		if (provider === IkeProviderType.EXISTING_SPAN) {
			providers.push(IkeCalcExistingSpanLegacyProvider);
		}
	}

	if (providers.length === 0) {
		return undefined;
	}

	return providers;
}

export function readSelectList(target: FormModelFieldSelectList, field: SchemaFormFieldSelectList) {
	if (field.options == null || !Array.isArray(field.options)) {
		return;
	}

	for (let i = 0; i < field.options.length; i++) {
		const opt = field.options[i];
		target.Mutator.option(opt, i);
	}
}

export function readSubForm(
	target: FormModelFieldSubForm,
	fromSchema: FormFieldSubFormSchema,
	allFields: { [fieldId: string]: boolean } = {}
) {
	for (const field of fromSchema.fields) {
		/** Sometimes we get duplicate fieldIds these are not valid and need to be removed. */
		if (allFields[field.name]) {
			continue;
		}

		const targetField = target.createField(field.type, {
			id: field.name,
			displayName: field.displayname,
			cloneable: field.cloneable,
			title: field.title,
			subtitle: field.subtitle,
			adminOnly: field.adminOnly,
			annotationLabelFieldId: field.annotationLabelFieldId,
			clearanceThresholdInMeters: field.clearanceThresholdInMeters,
			providers: getProviders(field),
			collector: field.collector,
			required: field.required,
			locked: field.locked,
			isGenerated: field.isGenerated,
		});

		allFields[field.name] = true;

		if (FormModelFieldSubForm.isSubForm(targetField)) {
			const schema = (<SchemaFormFieldSubForm>field).schema;
			if (schema == null) {
				continue;
			}

			targetField.Mutator.label(schema.label);
			if (!Array.isArray(schema.fields)) {
				continue;
			}

			readSubForm(targetField, schema, allFields);
		} else if (FormModelFieldNestedList.isNestedList(targetField)) {
			targetField.Mutator.init((<SchemaFormFieldNestedList>field).options);
		} else if (FormModelFieldSelectList.isSelectList(targetField)) {
			readSelectList(targetField, <SchemaFormFieldSelectList>field);
		}
	}

	if (fromSchema.subformType === 'span' && fromSchema.subformSpanTypeState) {
		target.Mutator.span.setFieldReferences(
			fromSchema.subformSpanTypeState.vectorFieldId,
			fromSchema.subformSpanTypeState.typeFieldId,
			fromSchema.subformSpanTypeState.midSpanIkePhotoFieldId
		);
	} else if (fromSchema.subformType === 'pole') {
		target.Mutator.poleType();
	}

	return null;
}

export function readParseDate(isoString) {
	if (isoString == null) {
		return null;
	}

	return new Date(isoString);
}
